/**
 * Link all (1.J stage 3, deliverable 33): a preview in place under the Unlinked heading that lists
 * every occurrence Link all would link, and one confirmed `hygiene.linkAll` for exactly those.
 * Cancel or Esc sends nothing and returns focus to "Link all".
 */
import { hygieneLinkAll, hygieneLinkAllPreview } from '@codex/contracts';
import { Button, Skeleton, TypeGlyph, useLayer, useReturnFocus } from '@codex/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../auth/client.js';
import { useNotify } from '../../shell/notices.js';
import { endSessionOn401 } from '../failure.js';
import { backlinksKey, unlinkedKey } from '../queries.js';
import { glyphFor } from '../story/glyph.js';
import {
  LINK_ALL_EMPTY_TEXT,
  LINK_ALL_FAILED_TEXT,
  LINK_ALL_PREVIEW_FAILED_TEXT,
  linkAllButtonText,
  linkAllResultText,
  toLinkAllRequest,
} from './link-all-model.js';

export function LinkAllPreview({
  docId,
  trigger,
  onClose,
}: {
  docId: string;
  trigger: HTMLElement | null;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const notify = useNotify();
  const region = useRef<HTMLElement>(null);
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  useLayer(true, onClose, 'form');
  useReturnFocus(true, trigger);
  useEffect(() => region.current?.focus(), []);

  const preview = useQuery({
    queryKey: ['links', 'link-all-preview', docId],
    queryFn: () => api(hygieneLinkAllPreview, { params: { id: docId } }),
    gcTime: 0,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const data = preview.data;

  const send = async () => {
    if (!data || data.occurrences.length === 0) return;
    setSending(true);
    setFailed(false);
    try {
      const result = await api(hygieneLinkAll, {
        params: { id: docId },
        body: toLinkAllRequest(data),
      });
      notify(linkAllResultText(result.linked, data.occurrences.length, data.total));
      await Promise.all([
        client.invalidateQueries({ queryKey: unlinkedKey(docId) }),
        client.invalidateQueries({ queryKey: backlinksKey(docId) }),
      ]);
      onClose();
    } catch (error) {
      if (!endSessionOn401(error)) setFailed(true);
      setSending(false);
    }
  };

  return (
    <section ref={region} tabIndex={-1} aria-label="Link all preview" className="cx-links-linkall">
      {preview.isError ? (
        <p role="alert">{LINK_ALL_PREVIEW_FAILED_TEXT}</p>
      ) : !data ? (
        <Skeleton lines={2} label="Loading the preview" />
      ) : data.occurrences.length === 0 ? (
        <p className="cx-links-story__empty">{LINK_ALL_EMPTY_TEXT}</p>
      ) : (
        <ul className="cx-links-tab__list" aria-label="Mentions to link">
          {data.occurrences.map((item) => (
            <li key={`${item.sourceDocId}:${item.blockId}:${item.ordinal}`}>
              <div className="cx-links-tab__item">
                <TypeGlyph type={glyphFor(item.sourceTypeKey)} />
                <span>{item.sourceTitle}</span>
                <span className="cx-links-tab__match">{item.text}</span>
              </div>
              {item.context && <p className="cx-links-tab__context">{item.context}</p>}
            </li>
          ))}
        </ul>
      )}
      {failed && <p role="alert">{LINK_ALL_FAILED_TEXT}</p>}
      <div className="cx-links-note-form__actions">
        {data && data.occurrences.length > 0 && (
          <Button variant="primary" size="sm" disabled={sending} onClick={() => void send()}>
            {linkAllButtonText(data.occurrences.length)}
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </section>
  );
}
